import React, { useCallback } from 'react';
import { NextButton } from 'components/Common/MUIStyledComponents';
import { useSnackbar } from 'notistack';
import { fileHandler, mapRowData } from 'utils/excelRenderer';
import { useDropzone, FileWithPath } from 'react-dropzone';

interface IStep2Props {
  file: FileWithPath | undefined;
  setFile: any;
  setRows: any;
  selectedTemp: string;
  setCols: any;
}

const Step2: React.FC<IStep2Props> = (props: IStep2Props) => {
  const { file, setFile, setRows, setCols } = props;
  const { enqueueSnackbar } = useSnackbar();

  const handleDeleteFile = () => {
    setFile(null);
    setRows([]);
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(async(accptFile) => {
      const reader = new FileReader();
      reader.onabort = () => {
        enqueueSnackbar('File reading aborted', { variant: 'info' });
      };
      reader.onerror = () => {
        enqueueSnackbar('Error reading file', { variant: 'error' });
      };
      setFile(accptFile);
      const { rows } = await fileHandler(accptFile);
      const colNames: string[] = rows[0];
      const rowData = rows.slice(1);
      const mappedRowData = mapRowData(colNames, rowData);
      setRows(mappedRowData);
      setCols(colNames);
    });
  }, []);

  const fileValidator = (file: any) => {
    if (file.size > 20000000) {
      enqueueSnackbar('Your file is too large, upload a file less than 20mbs', {
        variant: 'error',
      });
      return {
        code: 'size-too-large',
        message: 'file is larger than 20MB',
      };
    }
    return null;
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet , text/csv',
  });

  return (
    <div className='files-spacing'>
      <div {...getRootProps()}>
        <div
          className={!file ? 'files-container-column' : 'files-container-row'}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='32'
            height='32'
            viewBox='0 0 32 32'
            fill='none'
          >
            <path
              d='M16.9468 15.0527C16.82 14.9313 16.6705 14.8361 16.5068 14.7727C16.1822 14.6393 15.8181 14.6393 15.4935 14.7727C15.3298 14.8361 15.1803 14.9313 15.0535 15.0527L12.3868 17.7193C12.1358 17.9704 11.9947 18.3109 11.9947 18.666C11.9947 19.0211 12.1358 19.3616 12.3868 19.6127C12.6379 19.8638 12.9784 20.0048 13.3335 20.0048C13.6886 20.0048 14.0291 19.8638 14.2802 19.6127L14.6668 19.2127V22.666C14.6668 23.0196 14.8073 23.3588 15.0574 23.6088C15.3074 23.8589 15.6465 23.9993 16.0002 23.9993C16.3538 23.9993 16.6929 23.8589 16.943 23.6088C17.193 23.3588 17.3335 23.0196 17.3335 22.666V19.2127L17.7202 19.6127C17.8441 19.7377 17.9916 19.8368 18.1541 19.9045C18.3165 19.9722 18.4908 20.0071 18.6668 20.0071C18.8428 20.0071 19.0171 19.9722 19.1796 19.9045C19.3421 19.8368 19.4895 19.7377 19.6135 19.6127C19.7385 19.4887 19.8377 19.3413 19.9053 19.1788C19.973 19.0163 20.0079 18.842 20.0079 18.666C20.0079 18.49 19.973 18.3157 19.9053 18.1532C19.8377 17.9908 19.7385 17.8433 19.6135 17.7193L16.9468 15.0527ZM26.6668 11.9193C26.6529 11.7969 26.6261 11.6762 26.5868 11.5593V11.4393C26.5227 11.3023 26.4372 11.1762 26.3335 11.066L18.3335 3.06602C18.2233 2.9623 18.0973 2.87679 17.9602 2.81268H17.8268C17.6972 2.74335 17.5578 2.69391 17.4135 2.66602H9.3335C8.27263 2.66602 7.25521 3.08744 6.50507 3.83759C5.75492 4.58773 5.3335 5.60515 5.3335 6.66602V25.3327C5.3335 26.3935 5.75492 27.411 6.50507 28.1611C7.25521 28.9113 8.27263 29.3327 9.3335 29.3327H22.6668C23.7277 29.3327 24.7451 28.9113 25.4953 28.1611C26.2454 27.411 26.6668 26.3935 26.6668 25.3327V11.9993C26.6668 11.9993 26.6668 11.9993 26.6668 11.9193ZM18.6668 7.21268L22.1202 10.666H20.0002C19.6465 10.666 19.3074 10.5255 19.0574 10.2755C18.8073 10.0254 18.6668 9.6863 18.6668 9.33268V7.21268ZM24.0002 25.3327C24.0002 25.6863 23.8597 26.0254 23.6096 26.2755C23.3596 26.5255 23.0205 26.666 22.6668 26.666H9.3335C8.97987 26.666 8.64074 26.5255 8.39069 26.2755C8.14064 26.0254 8.00016 25.6863 8.00016 25.3327V6.66602C8.00016 6.31239 8.14064 5.97326 8.39069 5.72321C8.64074 5.47316 8.97987 5.33268 9.3335 5.33268H16.0002V9.33268C16.0002 10.3935 16.4216 11.411 17.1717 12.1611C17.9219 12.9113 18.9393 13.3327 20.0002 13.3327H24.0002V25.3327Z'
              fill='var(--alternative-text-color)'
            />
          </svg>
          <p>Select a file or drag and drop here</p>
          <input {...getInputProps()} />
          <NextButton>Select File</NextButton>
        </div>
      </div>
      {file ? (
        <div className='file-list'>
          <p>{file.path}</p>
          <div className='file-size-div'>
            <p>{Math.round((file.size / 1000) * 10) / 10}KB</p>
            <div className='cursor-pointer' onClick={handleDeleteFile}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
              >
                <path
                  d='M7.5 13.5C7.69891 13.5 7.88968 13.421 8.03033 13.2803C8.17098 13.1397 8.25 12.9489 8.25 12.75V8.25C8.25 8.05109 8.17098 7.86032 8.03033 7.71967C7.88968 7.57902 7.69891 7.5 7.5 7.5C7.30109 7.5 7.11032 7.57902 6.96967 7.71967C6.82902 7.86032 6.75 8.05109 6.75 8.25V12.75C6.75 12.9489 6.82902 13.1397 6.96967 13.2803C7.11032 13.421 7.30109 13.5 7.5 13.5ZM15 4.5H12V3.75C12 3.15326 11.7629 2.58097 11.341 2.15901C10.919 1.73705 10.3467 1.5 9.75 1.5H8.25C7.65326 1.5 7.08097 1.73705 6.65901 2.15901C6.23705 2.58097 6 3.15326 6 3.75V4.5H3C2.80109 4.5 2.61032 4.57902 2.46967 4.71967C2.32902 4.86032 2.25 5.05109 2.25 5.25C2.25 5.44891 2.32902 5.63968 2.46967 5.78033C2.61032 5.92098 2.80109 6 3 6H3.75V14.25C3.75 14.8467 3.98705 15.419 4.40901 15.841C4.83097 16.2629 5.40326 16.5 6 16.5H12C12.5967 16.5 13.169 16.2629 13.591 15.841C14.0129 15.419 14.25 14.8467 14.25 14.25V6H15C15.1989 6 15.3897 5.92098 15.5303 5.78033C15.671 5.63968 15.75 5.44891 15.75 5.25C15.75 5.05109 15.671 4.86032 15.5303 4.71967C15.3897 4.57902 15.1989 4.5 15 4.5ZM7.5 3.75C7.5 3.55109 7.57902 3.36032 7.71967 3.21967C7.86032 3.07902 8.05109 3 8.25 3H9.75C9.94891 3 10.1397 3.07902 10.2803 3.21967C10.421 3.36032 10.5 3.55109 10.5 3.75V4.5H7.5V3.75ZM12.75 14.25C12.75 14.4489 12.671 14.6397 12.5303 14.7803C12.3897 14.921 12.1989 15 12 15H6C5.80109 15 5.61032 14.921 5.46967 14.7803C5.32902 14.6397 5.25 14.4489 5.25 14.25V6H12.75V14.25ZM10.5 13.5C10.6989 13.5 10.8897 13.421 11.0303 13.2803C11.171 13.1397 11.25 12.9489 11.25 12.75V8.25C11.25 8.05109 11.171 7.86032 11.0303 7.71967C10.8897 7.57902 10.6989 7.5 10.5 7.5C10.3011 7.5 10.1103 7.57902 9.96967 7.71967C9.82902 7.86032 9.75 8.05109 9.75 8.25V12.75C9.75 12.9489 9.82902 13.1397 9.96967 13.2803C10.1103 13.421 10.3011 13.5 10.5 13.5Z'
                  fill='var(--alternative-text-color)'
                />
              </svg>
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default Step2;
